import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Mail, Lock, ShieldCheck } from 'lucide-react';
import { BrandMark } from '@/layouts/Shells';
import '@/layouts/shell.css';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import { Alert, Button, Input, Select } from '@/components/ui';

function AuthLayout({ children }: { children: ReactNode }) {
  return (
    <div className="auth">
      <div className="auth-form">
        <div className="auth-form-inner">
          <Link to="/" className="auth-brand"><BrandMark kind="wordmark" size={44} alt="Botifire" /></Link>
          {children}
        </div>
      </div>
      <aside className="auth-aside" aria-hidden>
        <svg className="flame-field" viewBox="0 0 600 800" preserveAspectRatio="xMidYMid slice">
          <defs><radialGradient id="glow" cx="80%" cy="15%" r="60%"><stop offset="0" stopColor="#fff" stopOpacity=".35" /><stop offset="1" stopColor="#fff" stopOpacity="0" /></radialGradient></defs>
          <rect width="600" height="800" fill="url(#glow)" />
        </svg>
        <div className="auth-demo">
          <div className="auth-bubble v"><small>Visitor · WhatsApp</small>Can I change the delivery address on order 4471?</div>
          <div className="auth-bubble ai"><small>AI agent · cites Shipping policy</small>Yes — until it ships. I've opened the change form for you.</div>
          <div className="auth-bubble v"><small>Visitor</small>It says it already shipped?</div>
          <div className="auth-bubble h"><small>Sana · human, took over</small>Thanks for waiting — I've asked the courier to reroute it.</div>
        </div>
        <blockquote>AI answers what it knows. Your team takes over the rest — in the same conversation.</blockquote>
        <div className="cite">Web, WhatsApp, Messenger, Instagram, Telegram, SMS and email in one inbox.</div>
      </aside>
    </div>
  );
}

const fieldErr = (e: ApiError | null, f: string) => e?.fields?.[f] ?? null;

export function Login() {
  const { me, setMe, setMfaPending } = useAuth();
  const { t } = useI18n();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);
  const next = params.get('next') || '/';
  if (me) return <Navigate to={next} replace />;
  const submit = async (e: FormEvent) => {
    e.preventDefault(); setBusy(true); setErr(null);
    try {
      const r = await api('/api/v1/auth/login', { body: { email, password }, workspace: null });
      if (r.mfa_required) { setMfaPending(true); nav('/mfa', { state: { from: next } }); }
      else { setMe(r); nav(next, { replace: true }); }
    } catch (e: any) { setErr(e); } finally { setBusy(false); }
  };
  return (
    <AuthLayout>
      <div className="stack" style={{ gap: 6 }}><h1>{t('auth.signin', 'Sign in')}</h1><p className="muted">Welcome back. Pick up where your team left off.</p></div>
      {params.get('reset') === '1' && !err && <Alert tone="success" title="Password changed.">Sign in with your new password. Other devices were signed out.</Alert>}
      {err && !Object.keys(err.fields).length && <Alert tone="danger">{err.message}</Alert>}
      <form className="stack-4" onSubmit={submit} noValidate>
        <Input label={t('auth.email', 'Email')} type="email" autoComplete="email" icon={<Mail />} value={email} onChange={(e) => setEmail(e.target.value)} error={fieldErr(err, 'email')} required autoFocus />
        <Input label={t('auth.password', 'Password')} type="password" autoComplete="current-password" icon={<Lock />} value={password} onChange={(e) => setPassword(e.target.value)} error={fieldErr(err, 'password')} required />
        <div className="row"><span className="spacer" /><Link className="small muted" to="/forgot-password">Forgot password?</Link></div>
        <Button variant="primary" size="lg" type="submit" loading={busy} block>{t('auth.signin', 'Sign in')}</Button>
      </form>
      <OAuthButtons next={next} />
      <p className="auth-switch">New to Botifire? <Link to="/signup">Create a workspace</Link></p>
    </AuthLayout>
  );
}

export function Signup() {
  const { me, setMe } = useAuth();
  const { locale } = useI18n();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [f, setF] = useState({ name: '', email: '', password: '', workspace_name: '', plan_code: params.get('plan') || 'starter' });
  const [err, setErr] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);
  if (me && !busy) return <Navigate to="/" replace />;
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const submit = async (e: FormEvent) => {
    e.preventDefault(); setBusy(true); setErr(null);
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
      const r = await api('/api/v1/auth/signup', { body: { ...f, locale, timezone: tz }, workspace: null });
      setMe(r);
      nav(`/w/${r.workspaces[0].slug}/onboarding`, { replace: true });
    } catch (e: any) { setErr(e); setBusy(false); }
  };
  return (
    <AuthLayout>
      <div className="stack" style={{ gap: 6 }}><h1>Create your workspace</h1><p className="muted">14-day trial. No card needed to start.</p></div>
      {err && !Object.keys(err.fields).length && <Alert tone="danger">{err.message}</Alert>}
      <form className="stack-4" onSubmit={submit} noValidate>
        <Input label="Your name" autoComplete="name" value={f.name} onChange={set('name')} error={fieldErr(err, 'name')} required autoFocus />
        <Input label="Work email" type="email" autoComplete="email" value={f.email} onChange={set('email')} error={fieldErr(err, 'email')} required />
        <Input label="Password" type="password" autoComplete="new-password" value={f.password} onChange={set('password')}
          hint="At least 10 characters with upper- and lower-case letters and a number." error={fieldErr(err, 'password') ?? (err?.code === 'weak_password' ? err.message : null)} required />
        <Input label="Company or workspace name" value={f.workspace_name} onChange={set('workspace_name')} error={fieldErr(err, 'workspace_name')} required />
        <Select label="Plan to trial" value={f.plan_code} onChange={set('plan_code')} options={[
          { value: 'starter', label: 'Starter — $29/mo after trial' }, { value: 'growth', label: 'Growth — $99/mo after trial' },
          { value: 'business', label: 'Business — $299/mo after trial' }]} />
        <Button variant="primary" size="lg" type="submit" loading={busy} block>Create workspace</Button>
      </form>
      <OAuthButtons next="/new-workspace" />
      <p className="auth-switch">Already have an account? <Link to="/login">Sign in</Link></p>
    </AuthLayout>
  );
}

export function VerifyEmail() {
  const [params] = useSearchParams();
  const [state, setState] = useState<'pending' | 'ok' | 'error'>('pending');
  const [msg, setMsg] = useState('');
  useEffect(() => {
    const token = params.get('token');
    if (!token) { setState('error'); setMsg('This link is missing its verification token.'); return; }
    api('/api/v1/auth/verify-email', { body: { token }, workspace: null }).then(() => setState('ok')).catch((e) => { setState('error'); setMsg(e.message); });
  }, [params]);
  return (
    <AuthLayout>
      <h1>Email verification</h1>
      {state === 'pending' && <p className="muted">Checking your link…</p>}
      {state === 'ok' && <Alert tone="success" title="Your email is verified.">You can close this tab or continue to your workspace.</Alert>}
      {state === 'error' && <Alert tone="danger" title="We couldn't verify this link.">{msg} Sign in and request a new link from Account & security.</Alert>}
      <Link to="/" className="btn btn-primary btn-lg">Continue</Link>
    </AuthLayout>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<ApiError | null>(null);
  const [devToken, setDevToken] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault(); setBusy(true); setErr(null);
    try { const r = await api('/api/v1/auth/forgot-password', { body: { email }, workspace: null }); setSent(true); setDevToken(r.dev_reset_token ?? null); }
    catch (e: any) { setErr(e); } finally { setBusy(false); }
  };
  return (
    <AuthLayout>
      <div className="stack" style={{ gap: 6 }}><h1>Reset your password</h1><p className="muted">We'll email you a link that works for one hour.</p></div>
      {sent ? (
        <Alert tone="success" title="Check your inbox.">If an account exists for {email}, a reset link is on its way.
          {devToken && <> <Link to={`/reset-password?token=${devToken}`}>Open reset link (development)</Link></>}</Alert>
      ) : (
        <form className="stack-4" onSubmit={submit}>
          {err && <Alert tone="danger">{err.message}</Alert>}
          <Input label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} icon={<Mail />} required autoFocus />
          <Button variant="primary" size="lg" type="submit" loading={busy} block>Send reset link</Button>
        </form>
      )}
      <p className="auth-switch"><Link to="/login">Back to sign in</Link></p>
    </AuthLayout>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<ApiError | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault(); setBusy(true); setErr(null);
    try { await api('/api/v1/auth/reset-password', { body: { token: params.get('token'), password }, workspace: null }); nav('/login?reset=1'); }
    catch (e: any) { setErr(e); } finally { setBusy(false); }
  };
  return (
    <AuthLayout>
      <div className="stack" style={{ gap: 6 }}><h1>Choose a new password</h1><p className="muted">You'll be signed out of every other device.</p></div>
      {err && <Alert tone="danger">{err.message}</Alert>}
      <form className="stack-4" onSubmit={submit}>
        <Input label="New password" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)}
          hint="At least 10 characters with upper- and lower-case letters and a number." required autoFocus />
        <Button variant="primary" size="lg" type="submit" loading={busy} block>Save password</Button>
      </form>
    </AuthLayout>
  );
}

export function MfaChallenge() {
  const { setMe, mfaPending, me } = useAuth();
  const nav = useNavigate();
  const loc = useLocation() as { state?: { from?: string } };
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<ApiError | null>(null);
  if (me) return <Navigate to={loc.state?.from || '/'} replace />;
  if (!mfaPending) return <Navigate to="/login" replace />;
  const submit = async (e: FormEvent) => {
    e.preventDefault(); setBusy(true); setErr(null);
    try { const r = await api('/api/v1/auth/mfa/verify', { body: { code }, workspace: null }); setMe(r); nav(loc.state?.from || '/', { replace: true }); }
    catch (e: any) { setErr(e); } finally { setBusy(false); }
  };
  return (
    <AuthLayout>
      <div className="stack" style={{ gap: 6 }}><h1>Two-step verification</h1><p className="muted">Enter the 6-digit code from your authenticator app, or one of your recovery codes.</p></div>
      {err && <Alert tone="danger">{err.message}</Alert>}
      <form className="stack-4" onSubmit={submit}>
        <Input label="Authentication code" inputMode="numeric" autoComplete="one-time-code" icon={<ShieldCheck />} value={code} onChange={(e) => setCode(e.target.value)} required autoFocus />
        <Button variant="primary" size="lg" type="submit" loading={busy} block>Verify</Button>
      </form>
    </AuthLayout>
  );
}

export function AcceptInvite() {
  const { token } = useParams();
  const { setMe, setMfaPending } = useAuth();
  const nav = useNavigate();
  const [info, setInfo] = useState<{ email: string; role: string; workspace: string; has_account: boolean } | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api(`/api/v1/auth/invitations/${token}`, { workspace: null }).then(setInfo).catch((e) => setLoadErr(e.message)); }, [token]);
  const submit = async (e: FormEvent) => {
    e.preventDefault(); setBusy(true); setErr(null);
    try {
      const r = await api('/api/v1/auth/invitations/accept', { body: { token, name, password }, workspace: null });
      if (r.mfa_required) { setMfaPending(true); nav('/mfa', { state: { from: '/' } }); return; }
      setMe(r);
      nav('/', { replace: true });
    } catch (e: any) { setErr(e); } finally { setBusy(false); }
  };
  return (
    <AuthLayout>
      {loadErr ? <><h1>Invitation unavailable</h1><Alert tone="danger">{loadErr}</Alert></> : !info ? <p className="muted">Loading invitation…</p> : (
        <>
          <div className="stack" style={{ gap: 6 }}><h1>Join {info.workspace}</h1><p className="muted">You've been invited as {info.role.replace('_', ' ')} with {info.email}.</p></div>
          {err && <Alert tone="danger">{err.message}</Alert>}
          <form className="stack-4" onSubmit={submit}>
            {!info.has_account && <Input label="Your name" value={name} onChange={(e) => setName(e.target.value)} required autoFocus />}
            <Input label={info.has_account ? 'Your Botifire password' : 'Choose a password'} type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
              hint={info.has_account ? undefined : 'At least 10 characters with upper- and lower-case letters and a number.'} />
            <Button variant="primary" size="lg" type="submit" loading={busy} block>Accept invitation</Button>
          </form>
        </>
      )}
    </AuthLayout>
  );
}

/* Slice 4: "Continue with Google / Microsoft" — rendered only for providers the platform has configured. */
function OAuthButtons({ next }: { next: string }) {
  const [providers, setProviders] = useState<{ id: string; name: string; start_url: string }[]>([]);
  const [params] = useSearchParams();
  const oauthError = params.get('oauth_error');
  useEffect(() => {
    api<{ id: string; name: string; start_url: string }[]>('/api/v1/auth/providers', { workspace: null }).then(setProviders).catch(() => setProviders([]));
  }, []);
  if (!providers.length && !oauthError) return null;
  return (
    <div className="stack" style={{ gap: 10 }}>
      {oauthError && <Alert tone="danger">{oauthError}</Alert>}
      {providers.length > 0 && <div className="row" style={{ gap: 10, color: 'var(--text-faint)', fontSize: 'var(--fs-sm)' }}><span style={{ flex: 1, height: 1, background: 'var(--border)' }} />or<span style={{ flex: 1, height: 1, background: 'var(--border)' }} /></div>}
      {providers.map((p) => (
        <a key={p.id} className="btn btn-secondary btn-lg btn-block" href={`${p.start_url}?next=${encodeURIComponent(next)}`}>Continue with {p.name}</a>
      ))}
    </div>
  );
}
